import { Show, For, createMemo } from 'solid-js'
import { A } from '@solidjs/router'
import { characterStore } from '/web/store'
import { getAssetUrl } from '/web/shared/util'
import {
  abbreviateNumber,
  getFanCount,
  getModelUrl,
  getRating,
} from '/web/shared/aifans/lib/model-util'

interface Props {
  categorySlug?: string
  currentId?: string
}

// Deterministic per-index corner badge (matches target design att-2).
// Only 4 badges exist in the design language; the 5th desktop card wraps back to HOT.
const BADGES = [
  { label: 'HOT', icon: '🔥', bg: '#A93FE0' },
  { label: 'NEW', icon: '⚡', bg: '#3B6FE0' },
  { label: 'VIP', icon: '', bg: '#3FB84D' },
  { label: 'TOP', icon: '👑', bg: '#E0A83F' },
]

const SuggestedCreators = (props: Props) => {
  const chars = characterStore((s) => ({ list: s.characters.list }))

  // Same-category creators first, then backfill with other creators (any category,
  // deterministic order = chars.list order) so the row always shows 5 cards when at
  // least 5 other creators exist site-wide. No duplicates, current creator excluded.
  const suggestions = createMemo(() => {
    const currentId = props.currentId
    if (currentId == null) return []

    const categorySlug = props.categorySlug
    const others = chars.list.filter((c) => c._id !== currentId)

    const sameCategory =
      categorySlug != null ? others.filter((c) => c.categorySlug === categorySlug) : []

    const result = sameCategory.slice(0, 5)
    const usedIds = new Set(result.map((c) => c._id))

    if (result.length < 5) {
      for (const c of others) {
        if (result.length >= 5) break
        if (usedIds.has(c._id)) continue
        result.push(c)
        usedIds.add(c._id)
      }
    }

    return result
  })

  return (
    <Show when={suggestions().length > 0}>
      {/* Same container geometry as the page content column (About Me grid + Subscribe card):
          heading + grid edges coincide with the Subscribe-To-Unlock card in both sidebar states. */}
      <section class="mx-auto w-full max-w-[var(--cf-content-max)] px-[var(--cf-gutter)] py-8 md:py-12">
        <h2 class="mb-6 font-bebas text-[26px] tracking-[1px] text-white md:mb-8">
          Profiles You Might Be Interested In
        </h2>
        <div class="grid grid-cols-2 gap-[14px] sm:gap-[22px] lg:grid-cols-5">
          <For each={suggestions()}>
            {(character, i) => {
              const badge = BADGES[i() % BADGES.length]
              const profileUrl = getModelUrl(
                character.categorySlug,
                character.slug || character._id
              )
              const fans = abbreviateNumber(getFanCount(character._id))
              const rating = getRating(character._id)

              return (
                <A
                  href={profileUrl}
                  class="group relative block overflow-hidden rounded-[14px] transition-transform duration-[250ms] hover:-translate-y-1"
                  classList={{ 'hidden lg:block': i() >= 4 }}
                >
                  {/* Portrait image */}
                  <div class="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={getAssetUrl(character.avatar || '', true, { width: 400 })}
                      alt={character.name}
                      class="h-full w-full object-cover transition-transform duration-[600ms] group-hover:scale-[1.06]"
                      style={{ 'object-position': 'center 18%' }}
                      loading="lazy"
                    />
                    {/* Corner badge */}
                    <span
                      class="absolute left-3 top-3 inline-flex items-center gap-1 rounded-[7px] px-2 py-1 font-rajdhani text-[11px] font-bold uppercase leading-none tracking-[0.5px] text-white"
                      style={{ background: badge.bg }}
                    >
                      <Show when={badge.icon}>
                        <span>{badge.icon}</span>
                      </Show>
                      {badge.label}
                    </span>
                  </div>

                  {/* Footer strip */}
                  <div class="bg-[#171430] px-4 py-[13px] lg:px-3 lg:py-[10px]">
                    <Show when={character.realName}>
                      <div class="mb-1 font-rajdhani text-[11px] font-bold uppercase tracking-[0.5px] text-[#4A9DE8] lg:text-[10px]">
                        {character.realName}
                      </div>
                    </Show>
                    <div class="font-bebas text-[18px] leading-[1.05] tracking-[0.5px] text-white lg:text-[16px]">
                      {character.name}
                    </div>
                    <div class="mt-2 flex items-center gap-2 font-rajdhani text-[13px] lg:mt-1.5 lg:text-[12px]">
                      <span class="font-bold text-white">
                        <span style={{ color: '#F5B301' }}>★</span> {rating}
                      </span>
                      <span class="text-[var(--cf-muted)]">{fans} fans</span>
                    </div>
                  </div>
                </A>
              )
            }}
          </For>
        </div>
      </section>
    </Show>
  )
}

export default SuggestedCreators
